import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import type { CatalogUploadData, CatalogUploadListItem } from '@src/apis/reware/catalog';

import { SOURCE_UPLOAD_TYPE_LABELS, STATUS_CHIP_COLORS, TERMINAL_STATUSES } from './constants';

interface UploadHistoryTableProps {
  uploads: CatalogUploadListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onCancelUpload: (uploadId: number) => void;
  onDownloadOriginal: (uploadId: number) => void;
  onDownloadErrorReport: (uploadId: number) => void;
  onRowClick?: (uploadId: number) => void;
}

const parseData = (data: unknown): CatalogUploadData => {
  if (!data || typeof data !== 'object') return {};
  return data as CatalogUploadData;
};

const formatValue = (value: number | undefined): string =>
  value != null ? String(value) : '\u2014';

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '\u2014';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const StatusCell: React.FC<{ status: string | null, data: CatalogUploadData }> = ({ status, data }) => {
  const label = status ?? 'unknown';
  const color = STATUS_CHIP_COLORS[label] ?? 'default';

  return (
    <Box>
      <Chip label={label} color={color} size="small" />
      {label === 'scheduled' && data.scheduledAt && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {'for '}
          {formatDate(data.scheduledAt)}
          {data.timezone && ` (${data.timezone})`}
        </Typography>
      )}
    </Box>
  );
};

const UploadHistoryTable: React.FC<UploadHistoryTableProps> = ({
  uploads,
  totalCount,
  page,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
  onCancelUpload,
  onDownloadOriginal,
  onDownloadErrorReport,
  onRowClick,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<CatalogUploadListItem | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: CatalogUploadListItem) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleCancel = () => {
    if (menuRow != null) {
      onCancelUpload(menuRow.id);
    }
    handleMenuClose();
  };

  const handleDownloadOriginal = () => {
    if (menuRow != null) {
      onDownloadOriginal(menuRow.id);
    }
    handleMenuClose();
  };

  const handleDownloadErrorReport = () => {
    if (menuRow != null) {
      onDownloadErrorReport(menuRow.id);
    }
    handleMenuClose();
  };

  const isCancelDisabled = (): boolean => {
    if (!menuRow?.uploadStatus) return true;
    // Normalize status to lowercase for comparison
    const normalizedStatus = menuRow.uploadStatus.toLowerCase();
    return TERMINAL_STATUSES.has(normalizedStatus);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Upload History
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Upload Name</TableCell>
              <TableCell>Upload Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Records Created</TableCell>
              <TableCell>Records Modified</TableCell>
              <TableCell>Records Deleted</TableCell>
              <TableCell>Errors</TableCell>
              <TableCell>Upload Initiated by</TableCell>
              <TableCell>Upload Initiated at</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && uploads.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && uploads.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">No uploads found.</Typography>
                </TableCell>
              </TableRow>
            )}
            {uploads.map((row) => {
              const data = parseData(row.data);
              return (
                <TableRow
                  key={row.id}
                  hover={!!onRowClick}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                  onClick={() => onRowClick?.(row.id)}
                >
                  <TableCell>{row.title ?? '\u2014'}</TableCell>
                  <TableCell>
                    {row.source ? (SOURCE_UPLOAD_TYPE_LABELS[row.source] ?? row.source) : '\u2014'}
                  </TableCell>
                  <TableCell>
                    <StatusCell status={row.uploadStatus} data={data} />
                  </TableCell>
                  <TableCell>{formatValue(data.added)}</TableCell>
                  <TableCell>{formatValue(data.updated)}</TableCell>
                  <TableCell>{formatValue(data.deleted)}</TableCell>
                  <TableCell>{formatValue(data.errorCount)}</TableCell>
                  <TableCell>{row.initiatedBy ?? '\u2014'}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, row);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCancel} disabled={isCancelDisabled()}>
          Cancel Upload
        </MenuItem>
        <MenuItem
          onClick={handleDownloadOriginal}
          disabled={!menuRow?.hasOriginalFile}
        >
          Download Original File
        </MenuItem>
        <MenuItem
          onClick={handleDownloadErrorReport}
          disabled={!parseData(menuRow?.data).errorCount}
        >
          Download Error Report
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UploadHistoryTable;
